import { graphql } from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import renderDocumentToReactComponents from '../../modules/contentful/renderDocumentToReactComponents/renderDocumentToReactComponents'
import ArrowLink from '../atoms/arrowLink'
import theme from '../atoms/theme'
import { Icon } from '../icon/icon'

type InfoCardProps = {
  infoCard: Queries.ContentfulInfoCard
  darkStyle?: boolean
}
//Mapping the long user friendly CMS names to shorter enums
enum IconNamesFromCMS {
  ACCESSIBILITY = 'Accesibility - Wheelchair Icon',
  MAP = 'Maps - Map Icon',
  PARKING = 'Parking - "P" Icon',
  CONE = 'Maintenance - Cone Icon',
}

//Mapping from the enums to icon names
const IconNames: Record<string, IconNamesFromCMS> = {
  AccessibleOutlined: IconNamesFromCMS.ACCESSIBILITY,
  MapOutlined: IconNamesFromCMS.MAP,
  LocalParkingOutlined: IconNamesFromCMS.PARKING,
  Cone: IconNamesFromCMS.CONE,
}

const { colours, breakpoints, icon } = theme

// Function to get the enum key based on the long value
const getIconKey = (value: string): string | undefined => {
  return Object.keys(IconNames).find((key) => IconNames[key] === value)
}

const StyledIcon = styled(Icon)`
  vertical-align: middle;
  margin-left: 1rem;
  display: inline-block;

  &.MuiSvgIcon-root {
    width: ${icon.medSmallSize};
    height: ${icon.medSmallSize};
    vertical-align: text-bottom;
  }

  &.hide-desktop {
    display: none;
  }

  @media only screen and ${breakpoints.toNormalScreen} {
    margin: 0 1rem 0 0;
    &.show-desktop {
      display: none;
    }
    &.hide-desktop {
      display: inline-block;
    }
  }
`

export const StyledInfoCard = styled.div`
  background-color: ${(props): string => (props.darkStyle ? '' : colours.light)};
  color: ${(props): string => (props.darkStyle ? colours.light : colours.dark)};
  display: flex;
  padding: 3rem 0;
  flex-basis: 75%;
  flex-direction: row;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
    padding: 0 0 0 0;
    margin-bottom: 3.6rem;
  }
`

export const Content = styled.div`
  padding: 0 2.4rem;
  display: flex;
  flex-direction: column;
  h3 {
    font-size: 2.4rem;
    line-height: 2.4rem;
    margin-bottom: 0;
    display: inline-block;
  }
  a {
    width: fit-content;
  }
  p + p {
    margin-top: 0;
  }
`

export const StyledTitle = styled.div`
  display: flex;
`

// calc = icon size + it's margin
export const StyledDescription = styled.div`
  @media only screen and ${breakpoints.toNormalScreen} {
    margin-left: calc(24px + 2rem);

    p:last-of-type {
      margin-bottom: 2.4rem;
    }

    a {
      font-size: 1.8rem;
    }
  }
`

const InfoCard = (props: InfoCardProps): JSX.Element => {
  const { title, description, link, linkUrl, icon } = props.infoCard

  return (
    <StyledInfoCard darkStyle={props.darkStyle}>
      {icon && <StyledIcon name={getIconKey(icon)} className='show-desktop' />}
      <Content>
        <StyledTitle>
          {icon && (
            <StyledIcon name={getIconKey(icon)} className='hide-desktop' />
          )}
          <h3>{title}</h3>
        </StyledTitle>
        <StyledDescription>
          {description?.raw &&
            renderDocumentToReactComponents(
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              description as { raw: string; references: any[] },
            )}
          {linkUrl && (
            <ArrowLink to={linkUrl}>
              <span>{link}</span>
            </ArrowLink>
          )}
        </StyledDescription>
      </Content>
    </StyledInfoCard>
  )
}

export default InfoCard

export const ContentfulInfoCardFragment = graphql`
  fragment ContentfulInfoCardFragment on ContentfulInfoCard {
    id
    __typename
    contentful_id
    title
    description {
      raw
    }
    link
    linkUrl
    icon
  }
`
